import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { Button, Typography } from "@mui/material";
import { GroupDataFromSearchResult } from "../../../interfaces/group.interface";
import { User } from "../../../interfaces/user.interface";
import AutocompleteComponent from "../../../pages/settings/Autocomplete";
import { addMembers, removeMembers } from "../../../services/group/group";
import { useToast } from "../../../context/Toast";
import { useAuth } from "../../../context/AuthContext";
import { getApiErrorMessage } from "../../../services/utils/apiError";
import { getInformedRequesterId } from "../../../services/utils/requester";

interface OptionType {
  id: string;
  label: string;
}

interface Props {
  show: boolean;
  onClose: () => void;
  user: User | null;
  allGroups: GroupDataFromSearchResult[];
}

const getGroupIdFromEntry = (entry: unknown): string | null => {
  if (typeof entry === "string") return entry.trim() ? entry : null;
  if (typeof entry === "number" && Number.isFinite(entry)) return String(entry);
  if (!entry || typeof entry !== "object") return null;

  const candidate = entry as Record<string, unknown>;
  const possibleKeys = ["id", "group_id", "groupId", "value"];

  for (const key of possibleKeys) {
    const value = candidate[key];

    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }

  const nestedKeys = ["Item", "group", "data"];

  for (const key of nestedKeys) {
    const nestedId = getGroupIdFromEntry(candidate[key]);
    if (nestedId) return nestedId;
  }

  return null;
};

const normalizeGroupIds = (groups: unknown): string[] => {
  if (!Array.isArray(groups)) {
    const singleGroupId = getGroupIdFromEntry(groups);
    return singleGroupId ? [singleGroupId] : [];
  }

  return groups
    .map((entry) => getGroupIdFromEntry(entry))
    .filter((id): id is string => Boolean(id));
};

export default function ModalUserGroups({
  show,
  onClose,
  user,
  allGroups,
}: Props) {
  const { t } = useTranslation('settings');
  const [selectedGroups, setSelectedGroups] = useState<OptionType[]>([]);
  const { showToast } = useToast();
  const { user: authUser } = useAuth();
  const informedRequesterId = getInformedRequesterId(authUser?.user?.id);

  const options: OptionType[] = useMemo(() => {
    const userGroupIds = normalizeGroupIds(user?.group);
    const userGroupIdSet = new Set(userGroupIds);

    return allGroups
      .filter((group) => group.status === 1 || userGroupIdSet.has(group.id))
      .map((group) => ({
        id: group.id,
        label: `${group.name} (${group.quantity_members} membro${group.quantity_members > 1 ? "s" : ""})`,
      }));
  }, [allGroups, user?.group]);

  const optionsById = useMemo(() => {
    return new Map(options.map((option) => [option.id, option] as const));
  }, [options]);

  useEffect(() => {
    if (!show || !user) {
      setSelectedGroups([]);
      return;
    }

    const userGroupIds = normalizeGroupIds(user.group);
    const mappedGroups = userGroupIds
      .map((groupId) => optionsById.get(groupId))
      .filter((option): option is OptionType => Boolean(option));

    setSelectedGroups(mappedGroups);
  }, [show, user, optionsById]);

  const handleChange = async (newSelected: OptionType[]) => {
    if (!user) return;

    const previousSelectedGroups = selectedGroups;
    const beforeIds = selectedGroups.map((group) => group.id);
    const afterIds = newSelected.map((group) => group.id);
    const addedId = afterIds.find((id) => !beforeIds.includes(id));

    setSelectedGroups(newSelected);

    if (!addedId) return;

    const addedGroup = optionsById.get(addedId);

    if (!addedGroup?.id) {
      setSelectedGroups(previousSelectedGroups);
      return;
    }

    if (!informedRequesterId) {
      showToast(t('groups.requesterMissing'), "error", 5000);
      setSelectedGroups(previousSelectedGroups);
      return;
    }

    try {
      const updatedGroup = (await addMembers(addedGroup.id, [user.id], informedRequesterId))?.group;

      if (updatedGroup) {
        showToast(t('userGroups.added', { name: updatedGroup.name }), 'success', 3500);
      }
    } catch (error) {
      console.error("Erro ao adicionar usuário ao grupo:", error);
      showToast(
        getApiErrorMessage(error, t('userGroups.linkError')),
        "error",
        5000
      );
      setSelectedGroups(previousSelectedGroups);
    }
  };

  const handleRemove = async (removedId: string) => {
    if (!user) return;

    const previousSelectedGroups = selectedGroups;
    setSelectedGroups((prev) => prev.filter((group) => group.id !== removedId));

    if (!informedRequesterId) {
      showToast(t('groups.requesterMissing'), "error", 5000);
      setSelectedGroups(previousSelectedGroups);
      return;
    }

    try {
      const updateResults = await removeMembers(removedId, [user.id], informedRequesterId);

      if (
        updateResults &&
        updateResults?.updatedGroup &&
        (updateResults?.updates?.usersRemovedFromGroupMembersList?.includes(user.id) ||
          updateResults?.updates?.usersWithGroupsUpdated?.includes(user.id))
      ) {
        showToast(t('userGroups.removed', { name: updateResults.updatedGroup.name }), 'success', 3500);
      }
    } catch (error) {
      console.error("Erro ao remover usuário do grupo:", error);
      showToast(
        getApiErrorMessage(error, t('userGroups.removeError')),
        "error",
        5000
      );
      setSelectedGroups(previousSelectedGroups);
    }
  };

  if (!show || !user) return null;

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header style={{ background: "#FFF" }} closeButton>
        <Modal.Title style={{ color: "#3C4043" }}>{t('userGroups.title')}</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: "#FFF", padding: "2em" }}>
        <Typography sx={{ fontSize: "1.1em", marginBottom: "0.8em", color: "#3C4043" }}>
          {t('userGroups.description')}
        </Typography>

        <AutocompleteComponent
          id={`group-selector-${user.id}`}
          options={options}
          selectedOptions={selectedGroups}
          onChange={handleChange}
          onRemove={handleRemove}
          label={String(t('userGroups.groups'))}
          placeholder={String(t('userGroups.selectGroups'))}
        />
      </Modal.Body>

      <Modal.Footer style={{ background: "#FFF" }}>
        <Button
          sx={{
            textTransform: "capitalize",
            padding: "0.5em 1.5em",
            borderRadius: "2em",
            fontSize: "1em",
            fontWeight: 500,
            border: "1px solid #a0a0a0",
            marginRight: "1em",
          }}
          variant="text"
          onClick={onClose}
        >
          {t('userGroups.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
